import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Sparkles } from 'lucide-react'

interface Satellite {
  readonly top: string
  readonly left: string
  // transform for the label pill relative to the node dot — keeps side
  // labels inside the panel on narrow screens
  readonly labelTransform: string
}

interface DemoTopic {
  readonly input: string
  readonly nodes: readonly string[]
}

// How long each topic stays on screen before the carousel advances.
const TOPIC_INTERVAL_MS = 4200

const ABOVE = 'translate(-50%, calc(-100% - 12px))'
const BELOW = 'translate(-50%, 12px)'

// Labeled nodes in two arcs (top + bottom) around the central question.
// Nothing sits on the horizontal axis, so the lines always reach a node and
// the centre title has room. Index matches each topic's nodes array.
const SATELLITES: readonly Satellite[] = [
  { top: '28%', left: '17%', labelTransform: ABOVE },
  { top: '17%', left: '50%', labelTransform: ABOVE },
  { top: '28%', left: '83%', labelTransform: ABOVE },
  { top: '76%', left: '19%', labelTransform: BELOW },
  { top: '85%', left: '50%', labelTransform: BELOW },
  { top: '76%', left: '81%', labelTransform: BELOW },
]

const CENTER = { x: 50, y: 50 }

const REST_TILT = { rx: 7, ry: 0 }

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function HeroDemo() {
  const { t } = useTranslation()
  const cardRef = useRef<HTMLDivElement>(null)
  const [tilt, setTilt] = useState(REST_TILT)
  const [activeTopic, setActiveTopic] = useState(0)
  const [paused, setPaused] = useState(false)

  const topics = t('landing.hero.demoTopics', { returnObjects: true }) as DemoTopic[]
  const safeTopics = Array.isArray(topics) && topics.length > 0 ? topics : []
  const current = safeTopics[activeTopic % Math.max(safeTopics.length, 1)] ?? { input: '', nodes: [] }
  const labels = current.nodes

  // Auto-rotate through the demo topics (carousel). Pauses for reduced motion
  // and while the pointer is hovering the 3D window.
  useEffect(() => {
    if (safeTopics.length <= 1 || prefersReducedMotion() || paused) return
    const id = window.setInterval(() => {
      setActiveTopic((i) => (i + 1) % safeTopics.length)
    }, TOPIC_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [safeTopics.length, paused])

  const handleMove = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    const el = cardRef.current
    if (!el || prefersReducedMotion()) return
    const rect = el.getBoundingClientRect()
    const px = (e.clientX - rect.left) / rect.width
    const py = (e.clientY - rect.top) / rect.height
    setTilt({
      rx: (0.5 - py) * 7 + 3,
      ry: (px - 0.5) * 10,
    })
  }, [])

  const handleEnter = useCallback(() => setPaused(true), [])
  const handleLeave = useCallback(() => {
    setTilt(REST_TILT)
    setPaused(false)
  }, [])

  const count = safeTopics.length
  const goTo = useCallback((i: number) => {
    if (count === 0) return
    setActiveTopic((i + count) % count)
  }, [count])

  return (
    <div className="hero-demo-stage relative mx-auto mt-16 w-full max-w-4xl">
      <div
        ref={cardRef}
        onMouseMove={handleMove}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className="hero-demo-card glass-panel relative rounded-[28px] p-4 sm:p-6"
        style={{ transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` }}
      >
        <div className="hero-demo-glow" aria-hidden />

        {/* Simulated research input */}
        <div className="hero-demo-input relative flex items-center gap-3 rounded-2xl border border-[color-mix(in_oklch,white_30%,transparent)] bg-[color-mix(in_oklch,var(--card)_60%,transparent)] px-4 py-3.5 shadow-[inset_0_1px_0_color-mix(in_oklch,white_40%,transparent)] backdrop-blur-md sm:px-5 sm:py-4">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <span key={activeTopic} className="landing-fade-in flex-1 truncate text-left text-base text-foreground/85 sm:text-lg">
            {current.input}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-xs font-medium text-primary-foreground sm:px-4 sm:text-sm">
            <Sparkles className="h-4 w-4" aria-hidden />
            {t('landing.hero.demoButton')}
          </span>
        </div>

        {/* Radial knowledge graph: central root topic + labeled nodes around it */}
        <div className="hero-demo-graph relative mt-4 overflow-hidden rounded-2xl border border-[color-mix(in_oklch,var(--foreground)_8%,transparent)] bg-[color-mix(in_oklch,var(--foreground)_3%,transparent)]">
          {/* keyed on the active topic so the draw + fade-in animations replay
              each time the carousel advances */}
          <div key={activeTopic} className="relative h-72 w-full sm:h-80">
            {/* connector lines from the centre to each node */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full text-foreground"
              aria-hidden
            >
              <g
                stroke="color-mix(in oklch, currentColor 38%, transparent)"
                fill="none"
              >
                {SATELLITES.map((s, i) => (
                  <line
                    key={i}
                    x1={CENTER.x}
                    y1={CENTER.y}
                    x2={parseFloat(s.left)}
                    y2={parseFloat(s.top)}
                    vectorEffect="non-scaling-stroke"
                    strokeWidth={1.4}
                    className="landing-graph-line"
                    style={{ animationDelay: `${0.2 + i * 0.12}s` }}
                  />
                ))}
              </g>
            </svg>

            {/* focal glow behind the root */}
            <div
              className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[color-mix(in_oklch,var(--foreground)_16%,transparent)] blur-2xl"
              aria-hidden
            />

            {/* satellite nodes — a dot sits exactly where the line ends, label offset beside it */}
            {SATELLITES.map((s, i) => (
              <div key={i}>
                {/* node dot on the line endpoint */}
                <div
                  className="absolute z-10"
                  style={{ top: s.top, left: s.left, transform: 'translate(-50%, -50%)' }}
                  aria-hidden
                >
                  <span
                    className="landing-fade-in block h-3 w-3 rounded-full border border-[color-mix(in_oklch,var(--foreground)_28%,transparent)] bg-[color-mix(in_oklch,var(--foreground)_60%,transparent)]"
                    style={{ animationDelay: `${0.4 + i * 0.12}s` }}
                  />
                </div>
                {/* label pill offset from the dot */}
                <div
                  className="absolute z-10"
                  style={{ top: s.top, left: s.left, transform: s.labelTransform }}
                >
                  <span
                    className="landing-fade-in glass-card inline-flex items-center whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium text-foreground/80 sm:px-2.5 sm:text-xs"
                    style={{ animationDelay: `${0.55 + i * 0.12}s` }}
                  >
                    {labels[i] ?? ''}
                  </span>
                </div>
              </div>
            ))}

            {/* central root topic */}
            <div
              className="absolute left-1/2 top-1/2"
              style={{ transform: 'translate(-50%, -50%)' }}
            >
              <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-2xl bg-primary px-3.5 py-2 text-[13px] font-semibold text-primary-foreground shadow-lg sm:px-4 sm:text-base">
                <span className="h-2 w-2 rounded-full bg-primary-foreground/80" aria-hidden />
                {current.input}
              </span>
            </div>
          </div>

        </div>

        {/* topic switch buttons (replaces the static hint) */}
        {safeTopics.length > 1 && (
          <div className="hero-demo-hint mt-4 flex flex-wrap items-center justify-center gap-2">
            {safeTopics.map((topic, i) => (
              <button
                key={topic.input}
                type="button"
                onClick={() => goTo(i)}
                aria-current={i === activeTopic}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
                  i === activeTopic
                    ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                    : 'border-[color-mix(in_oklch,var(--foreground)_12%,transparent)] text-muted-foreground hover:border-[color-mix(in_oklch,var(--foreground)_24%,transparent)] hover:text-foreground'
                }`}
              >
                {topic.input}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
